import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, onSnapshot, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import '../Admin.css';

function AddServices() {
  const [serviceName, setServiceName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState(''); // Stores uploaded image URL
  const [actualPrice, setActualPrice] = useState('');
  const [offerPrice, setOfferPrice] = useState('');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  // States for "Add More Details" Feature
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [activeServiceId, setActiveServiceId] = useState(null);
  const [extraDetails, setExtraDetails] = useState([
    { title: '', desc: '' },
    { title: '', desc: '' },
    { title: '', desc: '' }
  ]);
  const [savingDetails, setSavingDetails] = useState(false);

  // 🎯 NEW: States for "Edit Service" Feature
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editServiceId, setEditServiceId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editActualPrice, setEditActualPrice] = useState('');
  const [editOfferPrice, setEditOfferPrice] = useState('');
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState('');
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [updatingService, setUpdatingService] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "services"), orderBy("createdAt", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setServices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, []);

  // Handle Image File Selection & Preview for Adding
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Handle Image File Selection & Preview for Editing
  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditImageFile(file);
      setEditImagePreview(URL.createObjectURL(file));
    }
  };

  // Upload Image to PHP Server
  const uploadImageToServer = async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch('https://mrwhiteuae.com/upload_service_image.php', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (data.status === 'success') {
        return data.imageUrl;
      } else {
        throw new Error(data.message || 'Image upload failed');
      }
    } catch (error) {
      console.error('Image Upload Error:', error);
      alert('Failed to upload image to server');
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile && !imageUrl) {
      alert("Please select an image for the service.");
      return;
    }

    setLoading(true);
    try {
      let finalImageUrl = imageUrl;

      if (imageFile) {
        finalImageUrl = await uploadImageToServer(imageFile);
        if (!finalImageUrl) {
          setLoading(false);
          return;
        }
      }

      await addDoc(collection(db, "services"), {
        name: serviceName,
        imageUrl: finalImageUrl,
        actualPrice: Number(actualPrice),
        offerPrice: Number(offerPrice),
        createdAt: serverTimestamp(),
        details: []
      });

      // Reset Form
      setServiceName('');
      setActualPrice('');
      setOfferPrice('');
      setImageFile(null);
      setImagePreview(null);
      setImageUrl('');
      alert("Service added successfully!");
    } catch (error) {
      console.error("Error adding service: ", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this service?")) {
      await deleteDoc(doc(db, "services", id));
    }
  };

  // Open Edit Modal and Load Existing Service Info
  const handleOpenEditModal = (service) => {
    setEditServiceId(service.id);
    setEditName(service.name || '');
    setEditActualPrice(service.actualPrice || '');
    setEditOfferPrice(service.offerPrice || '');
    setExistingImageUrl(service.imageUrl || '');
    setEditImagePreview(service.imageUrl || '');
    setEditImageFile(null);
    setEditModalOpen(true);
  };

  // Save Updated Service to Firestore
  const handleUpdateService = async (e) => {
    e.preventDefault();
    setUpdatingService(true);

    try {
      let finalImageUrl = existingImageUrl;

      // Upload new image if selected
      if (editImageFile) {
        const uploadedUrl = await uploadImageToServer(editImageFile);
        if (uploadedUrl) {
          finalImageUrl = uploadedUrl;
        } else {
          setUpdatingService(false);
          return;
        }
      }

      const serviceRef = doc(db, "services", editServiceId);
      await updateDoc(serviceRef, {
        name: editName,
        actualPrice: Number(editActualPrice),
        offerPrice: Number(editOfferPrice),
        imageUrl: finalImageUrl,
      });

      setEditModalOpen(false);
      setEditServiceId(null);
      alert("Service updated successfully!");
    } catch (error) {
      console.error("Error updating service: ", error);
      alert("Failed to update service.");
    }
    setUpdatingService(false);
  };

  // Open Details Modal and load existing data
  const handleOpenDetails = (service) => {
    setActiveServiceId(service.id);
    if (service.details && service.details.length > 0) {
      const loadedDetails = [
        { title: service.details[0]?.title || '', desc: service.details[0]?.desc || '' },
        { title: service.details[1]?.title || '', desc: service.details[1]?.desc || '' },
        { title: service.details[2]?.title || '', desc: service.details[2]?.desc || '' }
      ];
      setExtraDetails(loadedDetails);
    } else {
      setExtraDetails([
        { title: '', desc: '' },
        { title: '', desc: '' },
        { title: '', desc: '' }
      ]);
    }
    setDetailModalOpen(true);
  };

  const handleDetailChange = (index, field, value) => {
    const updated = [...extraDetails];
    updated[index][field] = value;
    setExtraDetails(updated);
  };

  const handleSaveDetails = async (e) => {
    e.preventDefault();
    setSavingDetails(true);
    try {
      const filteredDetails = extraDetails.filter(item => item.title.trim() !== '' || item.desc.trim() !== '');
      
      const serviceRef = doc(db, "services", activeServiceId);
      await updateDoc(serviceRef, {
        details: filteredDetails
      });
      
      setDetailModalOpen(false);
      setActiveServiceId(null);
      alert("Details updated successfully!");
    } catch (error) {
      console.error("Error updating details: ", error);
    }
    setSavingDetails(false);
  };

  return (
    <div className="add-services-container">
      
      {/* Form Section */}
      <section className="form-section">
        <div className="form-card-full">
          <div className="form-header">
            <h3><i className="fas fa-plus-circle"></i> Add New Service</h3>
          </div>
          
          <form onSubmit={handleSubmit} className="horizontal-form">
            <div className="form-row">
              <div className="input-group">
                <label>Service Name</label>
                <input type="text" value={serviceName} onChange={(e)=>setServiceName(e.target.value)} placeholder="e.g. Dry Cleaning" required />
              </div>

              {/* Image Selection Field with Preview */}
              <div className="input-group">
                <label>Service Image</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageChange} 
                    required={!imageUrl} 
                    style={{ fontSize: '12px' }}
                  />
                  {imagePreview && (
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #ccc' }} 
                    />
                  )}
                </div>
              </div>

              <div className="input-group">
                <label>Actual Price (₹)</label>
                <input type="number" value={actualPrice} onChange={(e)=>setActualPrice(e.target.value)} required />
              </div>

              <div className="input-group">
                <label>Offer Price (₹)</label>
                <input type="number" value={offerPrice} onChange={(e)=>setOfferPrice(e.target.value)} required />
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Uploading & Saving...' : 'Add Service'}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Table Section */}
      <section className="table-section">
        <div className="table-card">
          <div className="table-header">
            <h3><i className="fas fa-list"></i> Service Price List</h3>
          </div>
          <div className="table-responsive">
            <table className="modern-admin-table">
              <thead>
                <tr>
                  <th>Sl No.</th>
                  <th>Image</th>
                  <th>Service Name</th>
                  <th>Actual Price</th>
                  <th>Offer Price</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {services.map((service, index) => (
                  <tr key={service.id}>
                    <td>{index + 1}</td>
                    <td>
                      {service.imageUrl ? (
                        <img 
                          src={service.imageUrl} 
                          alt={service.name} 
                          style={{ width: '45px', height: '45px', borderRadius: '10px', objectFit: 'cover', border: '1px solid #eee' }} 
                        />
                      ) : (
                        <div className="table-icon-circle"><i className="fas fa-image"></i></div>
                      )}
                    </td>
                    <td className="service-name-cell">{service.name}</td>
                    <td className="price-old">₹{service.actualPrice}</td>
                    <td className="price-new">₹{service.offerPrice}</td>
                    <td className="text-center">
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        {/* 🎯 EDIT BUTTON */}
                        <button onClick={() => handleOpenEditModal(service)} className="edit-action-btn" style={{ padding: '6px 12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button onClick={() => handleOpenDetails(service)} className="add-details-action-btn">
                          <i className="fas fa-info-circle"></i> Details
                        </button>
                        <button onClick={() => handleDelete(service.id)} className="delete-action-btn">
                          <i className="fas fa-trash-alt"></i> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 🎯 EDIT SERVICE MODAL */}
      {editModalOpen && (
        <div className="modal-overlay" onClick={() => setEditModalOpen(false)}>
          <div className="modern-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-head">
              <h4><i className="fas fa-edit"></i> Edit Service</h4>
              <button className="close-x" onClick={() => setEditModalOpen(false)}>&times;</button>
            </div>

            <form onSubmit={handleUpdateService} style={{ padding: '15px' }}>
              <div className="input-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Service Name</label>
                <input 
                  type="text" 
                  value={editName} 
                  onChange={(e) => setEditName(e.target.value)} 
                  required 
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>

              <div className="input-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Service Image</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleEditImageChange} 
                    style={{ fontSize: '12px' }}
                  />
                  {editImagePreview && (
                    <img 
                      src={editImagePreview} 
                      alt="Edit Preview" 
                      style={{ width: '45px', height: '45px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #ccc' }} 
                    />
                  )}
                </div>
              </div>

              <div className="input-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Actual Price (₹)</label>
                <input 
                  type="number" 
                  value={editActualPrice} 
                  onChange={(e) => setEditActualPrice(e.target.value)} 
                  required 
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>

              <div className="input-group" style={{ marginBottom: '15px' }}>
                <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Offer Price (₹)</label>
                <input 
                  type="number" 
                  value={editOfferPrice} 
                  onChange={(e) => setEditOfferPrice(e.target.value)} 
                  required 
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>

              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" onClick={() => setEditModalOpen(false)} style={{ padding: '8px 15px', background: '#e0e0e0', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" className="submit-btn" disabled={updatingService} style={{ padding: '8px 15px' }}>
                  {updatingService ? 'Updating...' : 'Update Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add More Details Modal */}
      {detailModalOpen && (
        <div className="modal-overlay" onClick={() => setDetailModalOpen(false)}>
          <div className="modern-modal details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h4><i className="fas fa-file-medical"></i> Add More Details (Max 3)</h4>
              <button className="close-x" onClick={() => setDetailModalOpen(false)}>&times;</button>
            </div>

            <form onSubmit={handleSaveDetails} className="modal-details-form">
              <div className="details-scroll-area" style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '5px' }}>
                {extraDetails.map((detail, index) => (
                  <div key={index} className="detail-row-group" style={{ marginBottom: '15px', borderBottom: '1px dashed #ddd', paddingBottom: '15px' }}>
                    <h5 style={{ marginBottom: '8px', color: '#555' }}>Detail Set {index + 1} <span style={{fontSize: '11px', color: '#aaa'}}>(Optional)</span></h5>
                    
                    <div className="input-group" style={{ marginBottom: '8px' }}>
                      <label style={{ fontSize: '12px' }}>Title</label>
                      <input 
                        type="text" 
                        value={detail.title} 
                        onChange={(e) => handleDetailChange(index, 'title', e.target.value)} 
                        placeholder="Page content Title Here" 
                      />
                    </div>
                    
                    <div className="input-group">
                      <label style={{ fontSize: '12px' }}>Description</label>
                      <textarea 
                        value={detail.desc} 
                        onChange={(e) => handleDetailChange(index, 'desc', e.target.value)} 
                        placeholder="Content description here"
                        rows="2"
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', resize: 'none' }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="modal-footer" style={{ marginTop: '15px', display: 'flex', justifyContent: 'end', gap: '10px' }}>
                <button type="button" className="cancel-btn" onClick={() => setDetailModalOpen(false)} style={{ padding: '8px 15px', background: '#e0e0e0', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" className="submit-btn" disabled={savingDetails} style={{ padding: '8px 15px' }}>
                  {savingDetails ? 'Saving...' : 'Save Details'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default AddServices;